// src/services/alarmas.js

import AuthService from "../auth/auth.js";
import DispositivosService from "./dispositivos.js";

class AlarmasService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";
    this.endpoint = "/alarmas/api/alarma/";

    // Cache de dispositivos
    this.dispositivosCache = new Map();
    this.cacheTiempo = 5 * 60 * 1000;
    this.ultimaActualizacionCache = null;

    // Cache de alarmas
    this.alarmasCache = [];
    this.alarmaCacheTime = null;
    this.alarmaCacheDuration = 30 * 1000; // 30 segundos
  }

  /**
   * OBTENER TODAS LAS ALARMAS DEL BACKEND
   * Sin filtros - trae todo para filtrar en frontend
   * @returns {Promise<Array>} Array con todas las alarmas
   */
  async obtenerTodasLasAlarmas() {
    // Verificar cache
    const ahora = Date.now();
    if (this.alarmaCacheTime && ahora - this.alarmaCacheTime < this.alarmaCacheDuration && this.alarmasCache.length > 0) {
      return [...this.alarmasCache];
    }

    // Request sin filtros
    const url = `${this.baseURL}${this.endpoint}`;
    const response = await AuthService.authenticatedRequest(url);

    // Mapear alarmas
    const alarmasMapeadas = (response.results || []).map((alarma) => ({
      ...alarma,
      mostrarDetalles: false,
    }));

    // Enriquecer con dispositivos
    const alarmasEnriquecidas = await this.enriquecerConDispositivos(alarmasMapeadas);

    // Actualizar cache
    this.alarmasCache = alarmasEnriquecidas;
    this.alarmaCacheTime = ahora;

    return alarmasEnriquecidas;
  }

  /**
   * OBTENER ALARMAS FILTRADAS (FRONTEND)
   * @param {Object} filtros - Filtros a aplicar
   * @returns {Promise<Array>} Array de alarmas filtradas
   */
  async obtenerAlarmasFiltradas(filtros = {}) {
    // Obtener todas las alarmas
    const todasLasAlarmas = await this.obtenerTodasLasAlarmas();

    // Aplicar filtros en frontend
    let resultado = [...todasLasAlarmas];

    // Filtro por prioridad
    if (filtros.prioridad) {
      resultado = resultado.filter((alarma) => alarma.prioridad === filtros.prioridad);
    }

    // Filtro por estado
    if (filtros.estado) {
      resultado = resultado.filter((alarma) => alarma.estado === filtros.estado);
    }

    // Filtro por tipo
    if (filtros.tipo) {
      resultado = resultado.filter((alarma) => alarma.tipo === filtros.tipo);
    }

    // Filtro por dispositivo
    if (filtros.dispositivo) {
      resultado = resultado.filter((alarma) => {
        // El dispositivo puede venir como ID o como objeto
        const dispositivoId = typeof alarma.dispositivo === "object" ? alarma.dispositivo?.id : alarma.dispositivo;
        return dispositivoId == filtros.dispositivo;
      });
    }

    // Búsqueda de texto
    if (filtros.busqueda) {
      const search = filtros.busqueda.toLowerCase();
      resultado = resultado.filter((alarma) => {
        const titulo = (alarma.titulo || "").toLowerCase();
        const descripcion = (alarma.descripcion || "").toLowerCase();
        return titulo.includes(search) || descripcion.includes(search);
      });
    }

    return resultado;
  }

  /**
   * OBTENER ALARMAS DE UN DISPOSITIVO ESPECÍFICO
   * @param {number|string} dispositivoId - ID del dispositivo
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Array>} Array de alarmas del dispositivo
   */
  async obtenerAlarmasDispositivo(dispositivoId, options = {}) {
    const { estado = "pendiente", limit = 10 } = options;

    // Obtener TODAS las alarmas (ya enriquecidas)
    const todasLasAlarmas = await this.obtenerTodasLasAlarmas();

    // Filtrar por dispositivo (manejar tanto ID como objeto)
    let resultado = todasLasAlarmas.filter((alarma) => {
      const idDispositivo = typeof alarma.dispositivo === "object" ? alarma.dispositivo?.id : alarma.dispositivo;
      return idDispositivo == dispositivoId;
    });

    // Filtrar por estado si se especifica
    if (estado) {
      resultado = resultado.filter((alarma) => alarma.estado === estado);
    }

    // Ordenar por fecha (más recientes primero)
    resultado.sort((a, b) => {
      const fechaA = new Date(a.fecha_creacion);
      const fechaB = new Date(b.fecha_creacion);
      return fechaB - fechaA;
    });

    // Limitar resultados
    const alarmasLimitadas = resultado.slice(0, limit);

    return alarmasLimitadas;
  }

  /**
   * OBTENER ESTADÍSTICAS (FRONTEND)
   * @param {Object} filtros - Filtros opcionales
   * @returns {Promise<Object>} Estadísticas de alarmas
   */
  async obtenerEstadisticas(filtros = {}) {
    try {
      // Obtener alarmas (filtradas o todas)
      const alarmas = Object.keys(filtros).length > 0 ? await this.obtenerAlarmasFiltradas(filtros) : await this.obtenerTodasLasAlarmas();

      const stats = {
        total: alarmas.length,
        criticas: alarmas.filter((a) => a.prioridad === "critica").length,
        altas: alarmas.filter((a) => a.prioridad === "alta").length,
        medias: alarmas.filter((a) => a.prioridad === "media").length,
        bajas: alarmas.filter((a) => a.prioridad === "baja").length,
      };

      // Para compatibilidad
      stats.advertencias = stats.altas;
      stats.informacion = stats.medias + stats.bajas;

      return stats;
    } catch (error) {
      return {
        total: 0,
        criticas: 0,
        altas: 0,
        medias: 0,
        bajas: 0,
        advertencias: 0,
        informacion: 0,
      };
    }
  }

  /**
   * INVALIDAR CACHE
   * Fuerza una nueva carga desde el backend
   */
  invalidarCache() {
    this.alarmasCache = [];
    this.alarmaCacheTime = null;
  }

  /**
   * ENRIQUECER CON DISPOSITIVOS
   */
  async enriquecerConDispositivos(alarmas) {
    try {
      await this.cargarDispositivosCache();

      return alarmas.map((alarma) => {
        // Si dispositivo es un número, buscar en cache
        if (typeof alarma.dispositivo === "number") {
          const dispositivo = this.dispositivosCache.get(alarma.dispositivo);

          if (dispositivo) {
            return {
              ...alarma,
              dispositivo: {
                id: dispositivo.id,
                nombre: dispositivo.nombre_dispositivo,
                ubicacion: dispositivo.ubicacion_dispositivo,
                estado: dispositivo.activo_dispositivo,
              },
            };
          }

          // Si no está en cache, crear objeto básico
          return {
            ...alarma,
            dispositivo: {
              id: alarma.dispositivo,
              nombre: `Dispositivo ${alarma.dispositivo}`,
              ubicacion: "Ubicación desconocida",
              estado: false,
            },
          };
        }

        // Si dispositivo ya es un objeto, dejarlo como está
        return alarma;
      });
    } catch (error) {
      return alarmas;
    }
  }

  /**
   * CARGAR CACHE DE DISPOSITIVOS
   */
  async cargarDispositivosCache() {
    try {
      const ahora = Date.now();

      if (this.ultimaActualizacionCache && ahora - this.ultimaActualizacionCache < this.cacheTiempo && this.dispositivosCache.size > 0) {
        return this.dispositivosCache;
      }

      const dispositivos = await DispositivosService.obtenerTodosLosDispositivos();

      this.dispositivosCache.clear();
      dispositivos.forEach((dispositivo) => {
        this.dispositivosCache.set(dispositivo.id, dispositivo);
      });

      this.ultimaActualizacionCache = ahora;

      return this.dispositivosCache;
    } catch (error) {
      return this.dispositivosCache;
    }
  }

  /**
   * ACTUALIZAR ESTADO DE ALARMA
   */
  async actualizarEstadoAlarma(id, nuevoEstado) {
    const response = await AuthService.authenticatedRequest(`${this.baseURL}${this.endpoint}${id}/`, {
      method: "PATCH",
      body: JSON.stringify({
        estado: nuevoEstado,
      }),
    });

    // Invalidar cache para que se recargue
    this.invalidarCache();

    return response;
  }
}

export default new AlarmasService();
